import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { DEFAULT_OFFSET_PAGE_SIZE, SocialCommentCategory, SocialNetwork, } from '@wit-support/admin-contracts';
import { Badge, Button, Card, DropdownMenu, EmptyState, Input, Label, LoadingTableCard, Pagination, RowActionsMenu, SELECT_ALL_VALUE, SelectField, Table, TextLink, pageCountOf, useDebouncedValue, } from '@wit-support/ui';
import { CONTENT_NETWORK_LABEL_RU, ListCard, NetworkChip, ResponsiveTableList, Time, } from '@wit-support/ui-domain';
import { FilterX, MessageSquareText, Search, UserRound } from 'lucide-react';
import { listComments } from '../api.js';
import { COMMENT_ACTION_KINDS, DISMISS_LABEL, DISMISSED_LABEL, NO_CAPABILITIES, actionUnavailableReason, canDismiss, pairGateReason, useCommentActions, } from '../comment-actions.js';
import { SOCIAL_ACTION_KIND_LABEL_RU } from '../outcome-text.js';
import { NEEDS_HUMAN_LABEL, SOCIAL_COMMENT_CATEGORY_LABEL_RU, SOCIAL_COMMENT_CATEGORY_ORDER, categoryLabel, categoryVariant, describeCommentSuggestion, describeSuggestion, humanSuggestionPending, langLabel, needsHuman, needsHumanAction, } from '../policy-text.js';
import { SOCIAL_KEY, SocialErrorState, SocialScreen, accountsOf } from '../shared.js';
import { LimitsSummary } from '../safety.js';
import { ShadowDecisionCell, ShadowSummaryBlock, shadowSeen, useShadowSummary, showShadowColumn, } from '../shadow.js';
import { ALGO_NO_ACTION_LABEL, DRY_NOT_SENT_RU, doneMarks, noActionLabel } from '../shadow-text.js';
import { ATTACHMENT_MARK_RU, ATTACHMENT_ONLY_TEXT_RU, commentAuthorLabel, tagsText, } from '../network-text.js';
import { readEnum, readPage, readPageSize, readString, normalizeSearch, useUrlSearch, } from '../url-search.js';
/**
 * Фильтр «Состояние» — так, как его понимает человек, а не как хранит движок.
 * `human` — ручная очередь «Нужен человек»: алгоритм отдал решение человеку
 * (вопросы к каналу, негатив, токсичное, спам без явной ссылки).
 */
export const COMMENT_FILTERS = ['new', 'human', 'pending', 'decided', 'ignored', 'shadow'];
export const COMMENT_FILTER_LABEL_RU = {
    new: 'Ждут решения',
    human: 'Нужен человек',
    pending: 'С предложениями',
    decided: 'Решённые',
    // «без действий» — и по отметке человека, и по решению алгоритма
    ignored: 'Без действий',
    // SHADOW-E: решённые алгоритмом в режиме «Тень» (движок `shadow=true`)
    shadow: 'Решено в тени',
};
export const COMMENT_PERIODS = ['1d', '7d', '30d'];
const PERIOD_LABEL_RU = {
    '1d': 'За сутки',
    '7d': 'За 7 дней',
    '30d': 'За 30 дней',
};
const PERIOD_DAYS = { '1d': 1, '7d': 7, '30d': 30 };
/** Сколько символов текста показывать в строке; полный — по клику. */
export const TEXT_PREVIEW_LENGTH = 140;
const SEARCH_DEBOUNCE_MS = 400;
/** Что попадает в ручную очередь — подсказка у кнопки «Ручная очередь». */
export const HUMAN_QUEUE_HINT = 'Комментарии, которые алгоритм отдал человеку: вопросы к каналу, негатив, оскорбления, спам без явной ссылки, упоминания нашего аккаунта.';
export const EMPTY_COMMENTS_TEXT = 'Комментариев ещё нет: движок опрашивает Instagram и Страницы Facebook каждые 20 минут после подключения токена.';
/**
 * Сети для фильтра «Сеть»: где реагирование уже сделано (есть адаптер), плюс
 * выбранная в адресе. Движок без строк (пусто) — все сети, как раньше.
 */
export function networkFilterOptions(status, selected) {
    const supported = new Set(status.items.filter((c) => c.supported).map((c) => c.network));
    if (selected)
        supported.add(selected);
    if (supported.size === 0)
        return [...SocialNetwork.options];
    return SocialNetwork.options.filter((n) => supported.has(n));
}
export function parseCommentsSearch(input) {
    const raw = normalizeSearch(input);
    return {
        account: readString(raw.account),
        network: readEnum(SocialNetwork.options, raw.network),
        filter: readEnum(COMMENT_FILTERS, raw.filter),
        category: readEnum(SocialCommentCategory.options, raw.category),
        period: readEnum(COMMENT_PERIODS, raw.period),
        q: readString(raw.q),
        page: readPage(raw.page),
        pageSize: readPageSize(raw.pageSize),
    };
}
/** Обратно в URL; значения по умолчанию не пишутся. */
export function serializeCommentsSearch(s) {
    return {
        account: s.account,
        network: s.network,
        filter: s.filter,
        category: s.category,
        period: s.period,
        q: s.q,
        page: s.page > 1 ? s.page : undefined,
        pageSize: s.pageSize !== DEFAULT_OFFSET_PAGE_SIZE ? s.pageSize : undefined,
    };
}
/** Фильтр экрана → параметры движка (`state` / `pending` / `needsHuman`). */
export function filterToQuery(f) {
    if (f === undefined)
        return {};
    if (f === 'pending')
        return { pending: true };
    if (f === 'human')
        return { needsHuman: true };
    if (f === 'shadow')
        return { shadow: true };
    return { state: f };
}
/** «Что умеем» аккаунта × сети из таблицы подключений. */
export function capabilitiesFor(status, account, network) {
    return (status.items.find((c) => c.account === account && c.network === network)?.capabilities ??
        NO_CAPABILITIES);
}
/** «Комментарии» — журнал комментариев под нашими постами: фильтры и страница в URL. */
export function SocialCommentsPage({ canManage }) {
    return (_jsx(SocialScreen, { title: "\u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0438", description: "\u041A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0438 \u043F\u043E\u0434 \u043D\u0430\u0448\u0438\u043C\u0438 \u043F\u043E\u0441\u0442\u0430\u043C\u0438, \u043A\u043E\u0442\u043E\u0440\u044B\u0435 \u0437\u0430\u0431\u0440\u0430\u043B \u0434\u0432\u0438\u0436\u043E\u043A. \u041E\u0442\u0441\u044E\u0434\u0430 \u043C\u043E\u0436\u043D\u043E \u043F\u043E\u0441\u0442\u0430\u0432\u0438\u0442\u044C \u0441\u0435\u0440\u0434\u0435\u0447\u043A\u043E, \u043E\u0442\u0432\u0435\u0442\u0438\u0442\u044C, \u0441\u043A\u0440\u044B\u0442\u044C \u0438\u043B\u0438 \u0443\u0434\u0430\u043B\u0438\u0442\u044C \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439.", children: (status) => _jsx(CommentsBody, { status: status, canManage: canManage }) }));
}
function CommentsBody({ status, canManage, }) {
    const [raw, setSearch] = useUrlSearch();
    const s = parseCommentsSearch(raw);
    const actions = useCommentActions();
    // «Тень за период»: один запрос сводки (byAccount) — и для блока, и для фильтра
    const shadow = useShadowSummary({ account: s.account, network: s.network });
    const update = (patch, opts = {}) => setSearch(serializeCommentsSearch({ ...s, ...patch, ...(opts.keepPage ? {} : { page: 1 }) }), {
        replace: opts.replace ?? false,
    });
    // Поиск: поле держит набранное, в адрес — после паузы ввода.
    const [draft, setDraft] = useState(s.q ?? '');
    const debounced = useDebouncedValue(draft, SEARCH_DEBOUNCE_MS);
    useEffect(() => {
        const next = debounced.trim().slice(0, 100);
        if (next !== (s.q ?? ''))
            update({ q: next || undefined }, { replace: true });
        // Реагируем только на паузу ввода.
    }, [debounced]);
    const q = useQuery({
        queryKey: [...SOCIAL_KEY, 'comments', s],
        // `from` считается в момент запроса, а не в ключе: иначе ключ менялся бы
        // на каждый рендер.
        queryFn: () => listComments({
            account: s.account,
            network: s.network,
            ...filterToQuery(s.filter),
            category: s.category,
            from: s.period !== undefined
                ? new Date(Date.now() - PERIOD_DAYS[s.period] * 86_400_000).toISOString()
                : undefined,
            q: s.q,
            page: s.page,
            pageSize: s.pageSize,
        }),
        placeholderData: keepPreviousData,
    });
    const total = q.data?.total ?? null;
    const items = q.data?.items ?? [];
    const lastPage = total !== null && total > 0 ? pageCountOf(total, s.pageSize) : null;
    const overflow = !q.isPlaceholderData && lastPage !== null && s.page > lastPage;
    useEffect(() => {
        if (overflow && lastPage !== null)
            update({ page: lastPage }, { keepPage: true, replace: true });
    }, [overflow, lastPage]);
    const hasFilters = s.account !== undefined ||
        s.network !== undefined ||
        s.filter !== undefined ||
        s.category !== undefined ||
        s.period !== undefined ||
        s.q !== undefined;
    const accounts = accountsOf(status);
    const capsOf = (c) => capabilitiesFor(status, c.account, c.network);
    const gateOf = (c) => pairGateReason(status, c.account, c.network);
    // «Алгоритм сделал бы» — только когда есть что показать: аккаунт в «Тени»
    // или у строки уже есть теневое решение.
    const shadowColumn = showShadowColumn(status, items);
    const headerCells = [
        'Дата',
        'Аккаунт',
        'Автор',
        'Текст',
        'Категория',
        'Пост',
        'Отметки',
        ...(shadowColumn ? [SHADOW_COLUMN_TITLE] : []),
        'Предложено',
        ...(canManage ? ['Действия'] : []),
    ];
    const header = (_jsx(Table.Header, { children: _jsx(Table.Row, { children: headerCells.map((h) => (_jsx(Table.HeaderCell, { children: h }, h))) }) }));
    return (_jsxs(_Fragment, { children: [_jsx(LimitsSummary, { items: status.items, account: s.account, network: s.network }), _jsx(ShadowSummaryBlock, { status: status, state: shadow, account: s.account, network: s.network }), _jsxs("div", { className: "flex flex-wrap items-end gap-3", children: [_jsx(SelectField, { id: "social-comments-account", label: "\u0410\u043A\u043A\u0430\u0443\u043D\u0442", value: s.account ?? SELECT_ALL_VALUE, onChange: (v) => update({ account: v === SELECT_ALL_VALUE ? undefined : v }), allLabel: "\u0412\u0441\u0435 \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u044B", options: [...new Set([...accounts, ...(s.account ? [s.account] : [])])].map((a) => ({
                            value: a,
                            label: a,
                        })) }), _jsx(SelectField, { id: "social-comments-network", label: "\u0421\u0435\u0442\u044C", value: s.network ?? SELECT_ALL_VALUE, onChange: (v) => update({ network: v === SELECT_ALL_VALUE ? undefined : SocialNetwork.parse(v) }), allLabel: "\u0412\u0441\u0435 \u0441\u0435\u0442\u0438", options: networkFilterOptions(status, s.network).map((n) => ({
                            value: n,
                            label: CONTENT_NETWORK_LABEL_RU[n],
                        })) }), _jsx(SelectField, { id: "social-comments-filter", label: "\u0421\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435", value: s.filter ?? SELECT_ALL_VALUE, onChange: (v) => update({ filter: v === SELECT_ALL_VALUE ? undefined : v }), allLabel: "\u0412\u0441\u0435", options: COMMENT_FILTERS.filter(
                        // «Решено в тени» — только когда тень есть (старый движок фильтр не знает)
                        // по сводке за период (byAccount), а не по режиму сейчас — RV-SHADOW M3
                        (f) => f !== 'shadow' ||
                            shadowSeen(status, shadow.query.data) ||
                            items.some((c) => c.shadow !== null) ||
                            s.filter === 'shadow').map((f) => ({ value: f, label: COMMENT_FILTER_LABEL_RU[f] })) }), _jsx(SelectField, { id: "social-comments-category", label: "\u041A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u044F", value: s.category ?? SELECT_ALL_VALUE, onChange: (v) => update({
                            category: v === SELECT_ALL_VALUE ? undefined : SocialCommentCategory.parse(v),
                        }), allLabel: "\u0412\u0441\u0435 \u043A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u0438", options: SOCIAL_COMMENT_CATEGORY_ORDER.map((c) => ({
                            value: c,
                            label: SOCIAL_COMMENT_CATEGORY_LABEL_RU[c],
                        })) }), _jsx(SelectField, { id: "social-comments-period", label: "\u041F\u0435\u0440\u0438\u043E\u0434", value: s.period ?? SELECT_ALL_VALUE, onChange: (v) => update({ period: v === SELECT_ALL_VALUE ? undefined : v }), allLabel: "\u0417\u0430 \u0432\u0441\u0451 \u0432\u0440\u0435\u043C\u044F", options: COMMENT_PERIODS.map((p) => ({ value: p, label: PERIOD_LABEL_RU[p] })) }), _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(Label, { htmlFor: "social-comments-q", children: "\u041F\u043E\u0438\u0441\u043A \u043F\u043E \u0442\u0435\u043A\u0441\u0442\u0443" }), _jsxs("div", { className: "relative", children: [_jsx(Search, { "aria-hidden": true, className: "pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" }), _jsx(Input, { id: "social-comments-q", type: "search", className: "pl-8", maxLength: 100, placeholder: "\u0421\u043B\u043E\u0432\u043E \u0438\u0437 \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u044F", value: draft, onChange: (e) => setDraft(e.target.value) })] })] }), _jsxs(Button, { type: "button", variant: s.filter === 'human' ? 'primary' : 'secondary', size: "sm", "aria-pressed": s.filter === 'human', title: HUMAN_QUEUE_HINT, onClick: () => update({ filter: s.filter === 'human' ? undefined : 'human' }), children: [_jsx(UserRound, { className: "mr-1 h-4 w-4", "aria-hidden": "true" }), " \u0420\u0443\u0447\u043D\u0430\u044F \u043E\u0447\u0435\u0440\u0435\u0434\u044C"] }), hasFilters ? (_jsxs(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => {
                            setDraft('');
                            setSearch(serializeCommentsSearch({ page: 1, pageSize: s.pageSize }));
                        }, children: [_jsx(FilterX, { className: "mr-1 h-4 w-4", "aria-hidden": "true" }), " \u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440\u044B"] })) : null] }), q.isError ? (_jsx(SocialErrorState, { error: q.error })) : q.isLoading ? (_jsx(LoadingTableCard, { header: header, colSpan: headerCells.length, rows: 5 })) : items.length === 0 ? (_jsx(Card, { children: _jsx(EmptyState, { icon: MessageSquareText, title: hasFilters ? 'Ничего не найдено' : 'Комментариев ещё нет', description: hasFilters
                        ? 'С этими фильтрами комментариев нет. Сбросьте фильтры или расширьте период.'
                        : EMPTY_COMMENTS_TEXT }) })) : (_jsxs(_Fragment, { children: [_jsx(ResponsiveTableList, { items: items, getKey: (c) => c.id, header: header, renderRow: (c) => (_jsxs(Table.Row, { children: [_jsx(Table.Cell, { className: "whitespace-nowrap text-[var(--color-text-muted)]", children: _jsx(Time, { iso: c.createdAt, preset: "shortNumeric" }) }), _jsx(Table.Cell, { children: _jsxs("span", { className: "flex flex-col gap-1", children: [c.account, " ", _jsx(NetworkChip, { network: c.network })] }) }), _jsx(Table.Cell, { children: _jsx(AuthorCell, { c: c }) }), _jsx(Table.Cell, { className: "max-w-[360px]", children: _jsx(CommentText, { c: c }) }), _jsx(Table.Cell, { children: _jsx(CategoryCell, { c: c }) }), _jsx(Table.Cell, { children: _jsx(PostLinkCell, { c: c }) }), _jsx(Table.Cell, { children: _jsx(CommentMarks, { c: c }) }), shadowColumn ? (_jsx(Table.Cell, { className: "max-w-[240px]", children: _jsx(ShadowDecisionCell, { c: c }) })) : null, _jsx(Table.Cell, { children: _jsx(ProposedCell, { c: c, canManage: canManage, actions: actions, gate: gateOf(c) }) }), canManage ? (_jsx(Table.Cell, { children: _jsx(CommentRowMenu, { c: c, caps: capsOf(c), actions: actions, gate: gateOf(c) }) })) : null] }, c.id)), renderMobileCard: (c) => (_jsxs(ListCard, { className: "flex flex-col gap-2 text-sm", children: [_jsxs("div", { className: "flex items-start justify-between gap-2", children: [_jsxs("span", { className: "flex flex-wrap items-center gap-2 text-[var(--color-text-muted)]", children: [_jsx(AuthorCell, { c: c }), " \u00B7 ", c.account, " ", _jsx(NetworkChip, { network: c.network }), _jsx(Time, { iso: c.createdAt, preset: "shortNumeric" })] }), canManage ? (_jsx(CommentRowMenu, { c: c, caps: capsOf(c), actions: actions, gate: gateOf(c) })) : null] }), _jsx(CommentText, { c: c }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(CategoryCell, { c: c }), _jsx(CommentMarks, { c: c }), _jsx(PostLinkCell, { c: c })] }), c.shadow !== null ? (_jsxs("span", { className: "flex flex-col gap-0.5", children: [_jsxs("span", { className: "text-xs font-medium", children: [SHADOW_COLUMN_TITLE, ":"] }), _jsx(ShadowDecisionCell, { c: c })] })) : null, _jsx(ProposedCell, { c: c, canManage: canManage, actions: actions, gate: gateOf(c) })] })) }), _jsx(Pagination, { page: s.page, pageSize: s.pageSize, total: total, isFetching: q.isFetching, onPageChange: (page) => update({ page }, { keepPage: true }), onPageSizeChange: (pageSize) => update({ pageSize }) })] })), actions.dialogs] }));
}
/** Автор: у Facebook — имя (username там нет), у Instagram — @username. */
export function AuthorCell({ c }) {
    const author = commentAuthorLabel(c);
    return (_jsxs("span", { className: "flex flex-wrap items-center gap-1", children: [author ?? _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u0430\u0432\u0442\u043E\u0440 \u0441\u043A\u0440\u044B\u0442" }), c.isOwn ? _jsx(Badge, { variant: "info", children: "\u043D\u0430\u0448" }) : null] }));
}
/** Текст: в строке — первые 140 символов и «Весь текст» в карточку комментария. */
function CommentText({ c }) {
    const long = c.text.length > TEXT_PREVIEW_LENGTH;
    return (_jsxs("span", { className: "flex flex-col gap-0.5", children: [c.text.trim() === '' && c.hasAttachment ? (_jsx("span", { className: "text-[var(--color-text-muted)]", children: ATTACHMENT_ONLY_TEXT_RU })) : (_jsx("span", { className: "break-words whitespace-pre-line", children: long ? `${c.text.slice(0, TEXT_PREVIEW_LENGTH)}…` : c.text })), _jsx(TextLink, { asChild: true, tone: "muted", className: "text-xs", children: _jsx(Link, { to: "/social/comments/$id", params: { id: c.id }, children: long ? 'Весь текст и журнал' : 'Карточка комментария' }) })] }));
}
/**
 * Категория и язык по алгоритму; «нужен человек» — если алгоритм отдал
 * комментарий человеку. Алгоритм не смотрел (`category: null`) — тире.
 */
export function CategoryCell({ c }) {
    const cat = categoryLabel(c.category);
    const lang = langLabel(c.lang);
    if (cat === null && lang === null && !needsHuman(c)) {
        return _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2014" });
    }
    return (_jsxs("span", { className: "flex flex-col items-start gap-1", children: [cat !== null && c.category !== null ? (_jsx(Badge, { variant: categoryVariant(c.category), children: cat })) : null, needsHuman(c) ? _jsx(Badge, { variant: "warning", children: NEEDS_HUMAN_LABEL }) : null, lang !== null ? (_jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u044F\u0437\u044B\u043A: ", lang] })) : null] }));
}
/** Внешняя ссылка на пост в сети (не карточка «Контента»); ссылки нет — тире. */
export function PostLinkCell({ c }) {
    if (!c.postUrl)
        return _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2014" });
    return (_jsx(TextLink, { href: c.postUrl, external: true, "aria-label": `Открыть пост: ${c.account} · ${CONTENT_NETWORK_LABEL_RU[c.network]}`, children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u043F\u043E\u0441\u0442" }));
}
/** Заголовок столбца теневого решения (RULINGS-SHADOW). */
export const SHADOW_COLUMN_TITLE = 'Алгоритм сделал бы';
/** Отметки «что мы уже сделали» — однозначно, от первого лица; только настоящие действия. */
export const LIKED_BY_US_MARK = 'сердечко поставлено';
export const REPLIED_BY_US_MARK = 'мы ответили';
export const HIDDEN_MARK = 'скрыт';
export const DELETED_MARK = 'удалён';
/** Сухое действие: в сеть не уходило — серым, без «мы ответили». */
const DRY_KIND_RU = {
    like: 'сердечко',
    reply: 'ответ',
    hide: 'скрыть',
    delete: 'удалить',
};
export const dryMarkText = (kind) => `${DRY_KIND_RU[kind]} — ${DRY_NOT_SENT_RU}`;
/** Старый движок без `doneDry`: отмечено выполненным, но ушло ли в сеть — не сказал. */
export const unknownMarkText = (kind) => `${DRY_KIND_RU[kind]} — отмечено, в сеть ли — не известно`;
const REAL_MARK = {
    like: { text: LIKED_BY_US_MARK, variant: 'success' },
    reply: { text: REPLIED_BY_US_MARK, variant: 'success' },
    hide: { text: HIDDEN_MARK, variant: 'warning' },
    delete: { text: DELETED_MARK, variant: 'danger' },
};
/**
 * Отметки: что сделано и что в тексте (упоминание, ссылка). Настоящие
 * действия — цветом («мы ответили»); отмеченные в сухом режиме — серым
 * «сухой режим, не отправлено» ({@link doneMarks}): в сеть они не уходили.
 * Решения тени здесь не показываются — у них свой столбец.
 */
export function CommentMarks({ c }) {
    const marks = [];
    // «мы ответили», а не «ответ»: рядом «ответов: N» и «упоминание», и голое
    // «ответ» читалось как «это ответ на другой комментарий».
    for (const m of doneMarks(c)) {
        marks.push(m.dry === true
            ? { key: `dry-${m.kind}`, text: dryMarkText(m.kind), variant: 'outline' }
            : m.dry === null
                ? { key: `unknown-${m.kind}`, text: unknownMarkText(m.kind), variant: 'neutral' }
                : { key: m.kind, ...REAL_MARK[m.kind] });
    }
    // Отметки людей (Facebook `message_tags`): «отметил: Имя». Движок кладёт их
    // имена и в `mentions`, поэтому при отметках отдельное «упоминание» не пишем.
    const tagged = tagsText(c.tags);
    if (tagged !== null)
        marks.push({ key: 'tags', text: tagged, variant: 'neutral' });
    else if (c.mentions.length > 0)
        marks.push({ key: 'mention', text: 'упоминание', variant: 'neutral' });
    if (c.hasAttachment)
        marks.push({ key: 'attachment', text: ATTACHMENT_MARK_RU, variant: 'neutral' });
    if (c.hasLink)
        marks.push({ key: 'link', text: 'ссылка', variant: 'warning' });
    const noAction = noActionLabel(c);
    if (noAction !== null)
        marks.push({
            key: 'ignored',
            text: noAction === 'human' ? DISMISSED_LABEL : ALGO_NO_ACTION_LABEL,
            variant: 'neutral',
        });
    if (marks.length === 0)
        return _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2014" });
    return (_jsx("span", { className: "flex flex-wrap gap-1", children: marks.map((m) => (_jsx(Badge, { variant: m.variant, children: m.text }, m.key))) }));
}
/**
 * Кнопка «Отметить просмотренным» — у комментария, который ждёт решения:
 * человек посмотрел и решил ничего не делать, комментарий уходит из «Ждут
 * решения». В сеть ничего не уходит.
 */
export function DismissButton({ c, actions }) {
    if (!canDismiss(c))
        return null;
    return (_jsx(Button, { type: "button", size: "sm", variant: "ghost", disabled: actions.busy, onClick: () => actions.dismiss(c), children: DISMISS_LABEL }));
}
/**
 * Предложения алгоритма: бейдж действия + «Подтвердить» / «Отклонить». Без
 * предложений у комментария, ждущего решения, — «Отметить просмотренным».
 */
export function ProposedCell({ c, canManage, actions, gate = null, }) {
    if (c.pending.length === 0) {
        const human = humanSuggestionPending(c) ? describeCommentSuggestion(c) : null;
        const dismiss = canManage && canDismiss(c) ? _jsx(DismissButton, { c: c, actions: actions }) : null;
        if (human) {
            return (_jsxs("span", { className: "flex flex-col items-start gap-1", children: [_jsxs("span", { className: "text-xs", children: [NEEDS_HUMAN_LABEL, ": ", human.what, human.why ? ` — ${human.why}` : ''] }), c.suggestion?.text ? (_jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u0447\u0435\u0440\u043D\u043E\u0432\u0438\u043A: \u00AB", c.suggestion.text.slice(0, 60), c.suggestion.text.length > 60 ? '…' : '', "\u00BB \u2014 \u0432 \u043A\u0430\u0440\u0442\u043E\u0447\u043A\u0435"] })) : null, dismiss] }));
        }
        return dismiss ?? _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2014" });
    }
    return (_jsx("span", { className: "flex flex-col gap-1.5", children: c.pending.map((a) => (_jsxs("span", { className: "flex flex-wrap items-center gap-1.5", children: [_jsxs(Badge, { variant: needsHumanAction(a) ? 'warning' : 'accent', children: [SOCIAL_ACTION_KIND_LABEL_RU[a.kind], a.text ? `: ${a.text.slice(0, 40)}${a.text.length > 40 ? '…' : ''}` : ''] }), needsHumanAction(a) ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: suggestionShort(a, c.category) })) : null, canManage ? (_jsxs(_Fragment, { children: [_jsx(Button, { type: "button", size: "sm", variant: "secondary", disabled: actions.busy || gate !== null, title: gate ??
                                (a.kind === 'reply' ? 'Откроется текст ответа — его можно поправить' : undefined), onClick: () => actions.approve(a, c), children: "\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0434\u0438\u0442\u044C" }), _jsx(Button, { type: "button", size: "sm", variant: "ghost", disabled: actions.busy, onClick: () => actions.reject(a, c), children: "\u041E\u0442\u043A\u043B\u043E\u043D\u0438\u0442\u044C" })] })) : null] }, a.id))) }));
}
/** «нужен человек: вопрос к каналу…» — коротко, для строки таблицы. */
function suggestionShort(a, category) {
    const s = describeSuggestion(a, category);
    if (s === null)
        return NEEDS_HUMAN_LABEL;
    return `${NEEDS_HUMAN_LABEL}: ${s.what}${s.why ? ` — ${s.why}` : ''}`;
}
/** Меню строки: доступные действия; недоступные — серые с причиной. */
function CommentRowMenu({ c, caps, actions, gate, }) {
    const queued = actions.queuedFor(c);
    return (_jsx(RowActionsMenu, { "aria-label": `Действия с комментарием ${commentAuthorLabel(c) ?? ''}`.trim(), children: COMMENT_ACTION_KINDS.map((kind) => {
            const reason = actionUnavailableReason(kind, c, caps, queued, gate);
            const label = kind === 'reply' ? 'Ответить' : SOCIAL_ACTION_KIND_LABEL_RU[kind];
            if (reason !== null) {
                return (_jsx(DropdownMenu.Item, { disabled: true, children: _jsxs("span", { className: "flex flex-col", children: [_jsx("span", { children: label }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: reason })] }) }, kind));
            }
            // Пока предыдущий запрос в пути — как кнопки карточки: второй клик дал
            // бы второй запрос и тост «Такое действие уже есть».
            if (actions.busy) {
                return (_jsx(DropdownMenu.Item, { disabled: true, children: label }, kind));
            }
            return kind === 'delete' ? (_jsx(RowActionsMenu.DangerItem, { onSelect: () => actions.start(c, kind), children: label }, kind)) : (_jsx(RowActionsMenu.Item, { onSelect: () => actions.start(c, kind), children: label }, kind));
        }) }));
}
//# sourceMappingURL=comments.js.map